import { useState } from "react";

import * as Sentry from "@sentry/react";

import {
  Grid,
  Tooltip,
  Avatar,
  IconButton,
  InputAdornment,
  TextField,
  useTheme
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShirt } from "@fortawesome/free-solid-svg-icons";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { LoadingButton } from "@aclymatepackages/atoms";
import { formatDecimal } from "@aclymatepackages/formatters";
import {
  isProductElectronic,
  findConvertedProductWeights,
  calculateProductFootprintBreakdown,
  electronicsSpecFields,
  migrateProductMaterialsToComponents
} from "@aclymatepackages/products";
import { displayProductFootprintCategories } from "@aclymatepackages/add-ons";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";

import ComponentsInterface from "../../inputs/products/ComponentsInterface";
import MaterialsInterface from "../../inputs/products/MaterialsInterface";
import ProductPackagingMaterials from "../../inputs/products/ProductPackagingMaterials";
import ProductRouteInput from "../../inputs/products/ProductRouteInput";
import ProductCategories from "../../inputs/products/ProductCategories";
import ProductSubcategories from "../../inputs/products/ProductSubcategories";
import ProductWeightInputRow from "../../inputs/products/ProductWeightInputRow";

import EditableImageUploadDropzone from "../../modules/file-uploaders/EditableImageUploadDropzone";

import NumberInput from "../../atoms/inputs/NumberInput";
import DetailsActionAlert from "../../atoms/notifications/DetailsActionAlert";
import SettingsObjectDetailsSlider from "../../platform-pages/dashboard/SettingsObjectDetailsSlider";

import { useCachedFirebaseCrud } from "../../../helpers/firebase";
import {
  isRouteComplete,
  areMaterialsValid,
  areComponentsValid,
  safeFetchReportSummary,
  isReportSummaryUsable
} from "../../../helpers/components/products";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";
import { useFetchProductReportSummary } from "../../../helpers/utils/apiCalls";
import { uploadImageData } from "../../../helpers/image-resizer";
import { getAccountId } from "../../../helpers/accountHelpers";

const findAreMaterialsArraysEqual = (originalMaterials, editedMaterials) => {
  if (originalMaterials.length !== editedMaterials.length) {
    return false;
  }

  return editedMaterials.every((editedMaterial, index) => {
    const originalMaterial = originalMaterials[index];
    return (
      editedMaterial.type === originalMaterial.type &&
      editedMaterial.weight === originalMaterial.weight
    );
  });
};

const TabFootprintDisplay = ({
  type,
  footprintKgs,
  errorMessage,
  footprintShared
}) => {
  const { palette } = useTheme();
  const [{ isSubscribed }] = useProductsEmissionsFactors();

  const { color, icon, name } =
    displayProductFootprintCategories.find(({ dataKey }) => dataKey === type) ||
    {};

  const buildText = () => {
    const footprintText = `Total ${name} Footprint: `;

    if (!footprintKgs) {
      return errorMessage;
    }

    if (!isSubscribed && !footprintShared) {
      return (
        <>
          {footprintText}
          <span style={{ filter: "blur(4px)" }}>####</span> kgs
        </>
      );
    }

    return `${footprintText} ${formatDecimal(footprintKgs)} kgs`;
  };

  return (
    <DetailsActionAlert
      color={footprintKgs ? color : palette.error.main}
      icon={footprintKgs ? icon : faTriangleExclamation}
      text={buildText()}
      typographyVariant="h5"
    />
  );
};

const BasicInfoTab = ({
  basicInfo,
  setBasicInfo,
  isEditedSkuDuplicate,
  lifecycleId,
  isInApp,
  readOnly,
  vendorDataVisible,
  allDisabled
}) => {
  const {
    sku,
    category,
    subcategory,
    productWeightUnit,
    productWeight,
    packagingWeight,
    grossWeight,
    imageUrl
  } = basicInfo;

  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const editProduct = (field) => (value) =>
    editObjectData(setBasicInfo, field, value);

  const onImageUploadDbCallback = async (imageUrl) => {
    updateCollectionDoc("product-lifecycle-events", lifecycleId, { imageUrl });
    return editProduct("imageUrl")(imageUrl);
  };

  if (readOnly && !vendorDataVisible) {
    return (
      <Grid container spacing={3} direction="column">
        {isInApp && !imageUrl && (
          <Grid item container justifyContent="center">
            <Grid item sx={{ width: 250, height: 250 }}>
              <EditableImageUploadDropzone
                imageName="Product Report Image"
                onSave={uploadImageData(onImageUploadDbCallback)}
                imageUrl={imageUrl}
                disabled={allDisabled}
              />
            </Grid>
          </Grid>
        )}
        {isInApp && (
          <Grid item>
            <TextField
              label="SKU (or other identifier)"
              value={sku || ""}
              setValue={editProduct("sku")}
              fullWidth
              disabled={allDisabled}
              error={isEditedSkuDuplicate}
              helperText={
                isEditedSkuDuplicate
                  ? "This SKU is already in use by another product."
                  : ""
              }
            />
          </Grid>
        )}
      </Grid>
    );
  }

  if (readOnly && vendorDataVisible) {
    return (
      <Grid container spacing={3} direction="column">
        {isInApp && (
          <>
            {!imageUrl && (
              <Grid item container justifyContent="center">
                <Grid item sx={{ width: 250, height: 250 }}>
                  <EditableImageUploadDropzone
                    imageName="Product Report Image"
                    onSave={uploadImageData(onImageUploadDbCallback)}
                    imageUrl={imageUrl}
                    disabled={allDisabled}
                  />
                </Grid>
              </Grid>
            )}
            <Grid item>
              <TextField
                label="SKU (or other identifier)"
                value={sku || ""}
                setValue={editProduct("sku")}
                fullWidth
                disabled={allDisabled}
                error={isEditedSkuDuplicate}
                helperText={
                  isEditedSkuDuplicate
                    ? "This SKU is already in use by another product."
                    : ""
                }
              />
            </Grid>
          </>
        )}
        <Grid item>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <ProductCategories
                value={category}
                setValue={(category) => {
                  editProduct("subcategory")(null);
                  return editProduct("category")(category);
                }}
                disabled
              />
            </Grid>
            <Grid item sm={6}>
              <ProductSubcategories
                value={subcategory}
                setValue={editProduct("subcategory")}
                category={category}
                disabled
              />
            </Grid>
            <Grid item>
              <ProductWeightInputRow
                weightObject={{ productWeight, packagingWeight, grossWeight }}
                setWeightObject={setBasicInfo}
                weightUnit={productWeightUnit}
                setWeightUnit={editProduct("productWeightUnit")}
                addendumSize="h4"
                disabled
                {...basicInfo}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} direction="column">
      {isInApp && (
        <>
          <Grid item container justifyContent="center">
            <Grid item sx={{ width: 250, height: 250 }}>
              <EditableImageUploadDropzone
                imageName="Product Report Image"
                onSave={uploadImageData(onImageUploadDbCallback)}
                imageUrl={imageUrl}
              />
            </Grid>
          </Grid>
          <Grid item>
            <TextField
              label="SKU (or other identifier)"
              value={sku || ""}
              setValue={editProduct("sku")}
              fullWidth
              error={isEditedSkuDuplicate}
              helperText={
                isEditedSkuDuplicate
                  ? "This SKU is already in use by another product."
                  : ""
              }
            />
          </Grid>
        </>
      )}
      <Grid item container spacing={2}>
        <Grid item sm={6}>
          <ProductCategories
            value={category}
            setValue={(category) => {
              editProduct("subcategory")(null);
              return editProduct("category")(category);
            }}
          />
        </Grid>
        <Grid item sm={6}>
          <ProductSubcategories
            value={subcategory}
            setValue={editProduct("subcategory")}
            category={category}
          />
        </Grid>
        <Grid item>
          <ProductWeightInputRow
            weightObject={{ productWeight, packagingWeight, grossWeight }}
            setWeightObject={setBasicInfo}
            weightUnit={productWeightUnit}
            setWeightUnit={editProduct("productWeightUnit")}
            addendumSize="h3"
            {...basicInfo}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const MaterialsTab = ({
  type,
  totalFootprintKgs,
  materials,
  setMaterials,
  productWeightUnit,
  totalMaterialsWeight,
  MaterialComponent,
  isInApp,
  disabled,
  footprintShared
}) => {
  const weightLabel =
    type === "packaging" ? "packaging weight" : "product weight";
  const errorMessage = totalMaterialsWeight
    ? `Add ${type} to calculate your footprint.`
    : `Enter your ${weightLabel} in the Specs tab before adding ${type}.`;

  return (
    <Grid container spacing={3} direction="column">
      {isInApp && (
        <Grid item>
          <TabFootprintDisplay
            type={type}
            footprintKgs={totalFootprintKgs}
            errorMessage={errorMessage}
            footprintShared={footprintShared}
          />
        </Grid>
      )}
      {(totalMaterialsWeight || (disabled && materials?.length > 0)) && (
        <>
          <Grid item>
            <MaterialsInterface
              type="materials"
              items={materials}
              setItems={setMaterials}
              MaterialComponent={MaterialComponent}
              productWeightUnit={productWeightUnit}
              totalWeight={totalMaterialsWeight}
              disabled={disabled}
              footprintShared={footprintShared}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

const EnergyTabInputRow = ({
  setValue,
  defaultValue,
  value,
  disabled,
  ...otherProps
}) => {
  return (
    <Grid item>
      <NumberInput
        setValue={setValue}
        value={value}
        fullWidth
        disabled={disabled}
        {...otherProps}
        InputProps={{
          endAdornment: value && value === defaultValue && (
            <InputAdornment position="end">
              <Tooltip title="This is the default value for this field if you leave it blank.">
                <HelpOutlineIcon />
              </Tooltip>
            </InputAdornment>
          )
        }}
      />
    </Grid>
  );
};

const EnergyUsageTab = ({
  totalFootprintKgs,
  electronics = {},
  setElectronics,
  isInApp,
  disabled,
  footprintShared
}) => {
  const updateElectronics = (field) => (value) =>
    editObjectData(setElectronics, field, value);

  return (
    <Grid container spacing={3} direction="column">
      {isInApp && (
        <Grid item>
          <TabFootprintDisplay
            type="energy"
            footprintKgs={totalFootprintKgs}
            errorMessage="Please complete the energy fields to calculate footprint."
            footprintShared={footprintShared}
          />
        </Grid>
      )}
      {electronicsSpecFields.map(({ field, ...otherProps }) => (
        <EnergyTabInputRow
          key={field}
          value={electronics[field]}
          setValue={updateElectronics(field)}
          disabled={disabled}
          {...otherProps}
        />
      ))}
    </Grid>
  );
};

const RoutesTab = ({
  route,
  setRoute,
  totalFootprintKgs,
  isInApp,
  disabled,
  footprintShared
}) => {
  const editRoute = (updateObj) => {
    const updatedRoute = { ...route, ...updateObj };
    return setRoute(updatedRoute);
  };

  return (
    <Grid container spacing={3} direction="column">
      {isInApp && (
        <Grid item>
          <TabFootprintDisplay
            type="travel"
            footprintKgs={totalFootprintKgs}
            errorMessage="Please complete all routes to calculate footprint."
            footprintShared={footprintShared}
          />
        </Grid>
      )}
      <Grid item>
        <ProductRouteInput
          route={route}
          editRoute={editRoute}
          disabled={disabled}
          footprintShared={footprintShared}
        />
      </Grid>
    </Grid>
  );
};

const ProductDetailsFooter = ({
  onSave,
  isEditedProductDifferent,
  saveLoading,
  subcategory,
  components,
  packaging,
  route,
  onDelete,
  productWeight,
  packagingWeight,
  isDuplicateSku,
  readOnly
}) => {
  const hasValidComponents = areComponentsValid(components, productWeight);
  const hasValidPackaging = areMaterialsValid(packaging, packagingWeight);

  return (
    <Grid
      container
      justifyContent={onDelete ? "space-between" : "flex-end"}
      wrap="nowrap"
    >
      {onDelete && (
        <Grid item>
          <IconButton onClick={() => onDelete()}>
            <DeleteIcon color="error" />
          </IconButton>
        </Grid>
      )}
      <Grid item>
        <LoadingButton
          isLoading={saveLoading}
          onClick={onSave}
          variant="contained"
          color="primary"
          label="Save Changes"
          disabled={
            !isEditedProductDifferent ||
            isDuplicateSku ||
            (!readOnly &&
              (!hasValidComponents ||
                !hasValidPackaging ||
                !isRouteComplete(route) ||
                !subcategory))
          }
        />
      </Grid>
    </Grid>
  );
};

const ProductDetailsSlider = ({
  selectedProduct,
  setSelectedProduct,
  otherProducts = [],
  additionalTabs = [],
  additionalLifecyleProps,
  additionalProductProps,
  additionalIsProductDifferent,
  LeftContentComponent,
  leftContentProps,
  isInApp,
  onDelete,
  onSaveSuccess,
  pendingNote,
  warningNote,
  readOnly = false,
  disabled = false,
  footprintShared = false,
  lifecycleCollectionName = "product-lifecycle-events",
  productCollectionName = "product-components",
  headerAction
}) => {
  const {
    id,
    lifecycleId,
    name,
    sku: originalSku,
    category: originalCategory,
    subcategory: originalSubcategory,
    productWeightUnit: originalProductWeightUnit,
    productWeight: originalProductWeight,
    packagingWeight: originalPackagingWeight,
    grossWeight: originalGrossWeight,
    materials: originalMaterials,
    componentRefs: originalComponentRefs,
    resolvedComponents,
    packaging: originalPackaging,
    electronics: originalElectronics,
    route: originalRoute,
    kgsCo2e: originalKgsCo2e,
    imageUrl: originalImageUrl,
    buyerIds: parentBuyerIds = [],
    creatorId: parentCreatorId,
    vendorIds: parentVendorIdsFromDoc = []
  } = selectedProduct;

  const originalBasicInfo = {
    name,
    sku: originalSku,
    category: originalCategory,
    subcategory: originalSubcategory,
    productWeightUnit: originalProductWeightUnit || "g",
    productWeight: originalProductWeight,
    packagingWeight: originalPackagingWeight,
    grossWeight:
      originalGrossWeight ||
      (originalProductWeight && originalPackagingWeight
        ? Number(originalProductWeight) + Number(originalPackagingWeight)
        : undefined),
    imageUrl: originalImageUrl
  };

  const { palette } = useTheme();

  const { updateCollectionDoc, newCollectionDoc } = useCachedFirebaseCrud();
  const [{ emissionsFactors }] = useProductsEmissionsFactors();
  const fetchProductReportSummary = useFetchProductReportSummary();

  const [basicInfo, setBasicInfo] = useState(originalBasicInfo);
  const [editableComponents, setEditableComponents] = useState(() => {
    if (resolvedComponents?.length) {
      return resolvedComponents.map((c) => ({
        firestoreId: c.id,
        localId: c.id,
        name: c.name,
        materials: c.materials || [],
        productWeight: c.productWeight || 0
      }));
    }
    if (originalMaterials?.length) {
      return migrateProductMaterialsToComponents(originalMaterials).map(
        (c) => ({
          firestoreId: null,
          localId: crypto.randomUUID(),
          ...c,
          productWeight: originalProductWeight || 0
        })
      );
    }
    return [
      {
        firestoreId: null,
        localId: crypto.randomUUID(),
        name: "",
        materials: [],
        productWeight: 0
      }
    ];
  });
  const [editablePackaging, setEditablePackaging] = useState(
    originalPackaging || []
  );
  const [editableElectronics, setEditableElectronics] =
    useState(originalElectronics);
  const [editableRoute, setEditableRoute] = useState(() => {
    if (!originalRoute?.destinations?.length) {
      return { destinations: [{}, {}], legs: [{}] };
    }
    const expectedLegsCount = originalRoute.destinations.length - 1;
    const legs =
      originalRoute.legs?.length === expectedLegsCount
        ? originalRoute.legs
        : Array(expectedLegsCount)
            .fill(null)
            .map(() => ({}));
    return { ...originalRoute, legs };
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const {
    category,
    subcategory,
    productWeightUnit,
    productWeight,
    packagingWeight,
    grossWeight,
    sku
  } = basicInfo;

  const isEditedSkuDuplicate = otherProducts.some(
    (product) => product.sku === sku
  );

  const editableProduct = {
    ...basicInfo,
    ...findConvertedProductWeights(basicInfo),
    components: editableComponents.map((c) => ({ ...c, productWeightUnit })),
    packaging: editablePackaging,
    electronics: editableElectronics,
    route: editableRoute
  };

  const safeCalculateFootprint = (product, factors) => {
    try {
      return calculateProductFootprintBreakdown(product, factors);
    } catch {
      return { kgsCo2e: 0, footprintBreakdownKgs: {} };
    }
  };

  const {
    kgsCo2e: editedKgsCo2e,
    footprintBreakdownKgs: editedFootprintBreakdownKgs,
    components: editedComponentFootprints = []
  } = safeCalculateFootprint(editableProduct, emissionsFactors);

  const calculatedEditableProduct = {
    ...editableProduct,
    kgsCo2e: editedKgsCo2e,
    footprintBreakdownKgs: editedFootprintBreakdownKgs
  };

  const onSave = async () => {
    setSaveLoading(true);

    try {
      if (readOnly) {
        await Promise.all([
          updateCollectionDoc("product-lifecycle-events", lifecycleId, {
            sku,
            ...additionalLifecyleProps
          }),
          ...(Object.keys(additionalProductProps || {}).length
            ? [
                updateCollectionDoc(
                  productCollectionName,
                  id,
                  additionalProductProps
                )
              ]
            : [])
        ]);
        if (onSaveSuccess) {
          return onSaveSuccess();
        }
        return setSelectedProduct({
          ...selectedProduct,
          ...additionalLifecyleProps,
          ...additionalProductProps
        });
      }

      const reportSummary = await safeFetchReportSummary({
        fetchReportSummary: fetchProductReportSummary,
        payload: calculatedEditableProduct,
        onError: (summaryError) =>
          Sentry.captureException(summaryError, {
            tags: { feature: "report-summary-fetch" },
            extra: { productId: id, lifecycleId }
          })
      });

      const sharedLifecycleUpdate = {
        route: editableRoute,
        packaging: editablePackaging,
        sku,
        packagingWeight,
        ...(isReportSummaryUsable(reportSummary) ? { reportSummary } : {}),
        ...additionalLifecyleProps
      };

      if (productCollectionName === "product-components") {
        const accountId = getAccountId();
        const originalComponentIds = (originalComponentRefs || []).map(
          ({ id: cId }) => cId
        );

        const componentBuyerIds = [...new Set([accountId, ...parentBuyerIds])];
        const componentCreatorId = parentCreatorId || accountId;
        const componentVendorIds =
          additionalProductProps?.vendorIds ?? parentVendorIdsFromDoc;

        const savedComponents = await Promise.all(
          editableComponents.map(async (comp) => {
            const componentDoc = {
              name: comp.name,
              materials: comp.materials,
              productWeight: comp.productWeight,
              productWeightUnit,
              creatorId: componentCreatorId,
              buyerIds: componentBuyerIds,
              vendorIds: componentVendorIds,
              parentProductId: id,
              archived: false
            };
            if (!comp.firestoreId) {
              const newId = await newCollectionDoc(
                "product-components",
                componentDoc
              );
              return { ...comp, firestoreId: newId };
            }
            await updateCollectionDoc(
              "product-components",
              comp.firestoreId,
              componentDoc
            );
            return comp;
          })
        );

        const deletedComponentIds = originalComponentIds.filter(
          (origId) => !editableComponents.some((c) => c.firestoreId === origId)
        );

        await Promise.all([
          ...deletedComponentIds.map((cId) =>
            updateCollectionDoc("product-components", cId, { archived: true })
          ),
          updateCollectionDoc(productCollectionName, id, {
            componentRefs: savedComponents.map((c) => ({ id: c.firestoreId })),
            category,
            subcategory,
            electronics: editableElectronics,
            productWeightUnit,
            productWeight,
            grossWeight,
            ...additionalProductProps
          }),
          updateCollectionDoc(
            lifecycleCollectionName,
            lifecycleId,
            sharedLifecycleUpdate
          )
        ]);

        if (onSaveSuccess) {
          return onSaveSuccess();
        }
        return setSelectedProduct({
          ...calculatedEditableProduct,
          componentRefs: savedComponents.map((c) => ({ id: c.firestoreId })),
          resolvedComponents: savedComponents.map((c) => ({
            id: c.firestoreId,
            name: c.name,
            materials: c.materials,
            productWeight: c.productWeight,
            productWeightUnit
          })),
          ...(isReportSummaryUsable(reportSummary) ? { reportSummary } : {})
        });
      }

      const flatMaterials = editableComponents.flatMap(
        (comp) => comp.materials || []
      );

      await Promise.all([
        updateCollectionDoc(productCollectionName, id, {
          materials: flatMaterials,
          category,
          subcategory,
          electronics: editableElectronics,
          productWeightUnit,
          productWeight,
          grossWeight,
          ...additionalProductProps
        }),
        updateCollectionDoc(
          lifecycleCollectionName,
          lifecycleId,
          sharedLifecycleUpdate
        )
      ]);

      if (onSaveSuccess) {
        return onSaveSuccess();
      }
      return setSelectedProduct({
        ...calculatedEditableProduct,
        ...(isReportSummaryUsable(reportSummary) ? { reportSummary } : {})
      });
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  const {
    materials: materialsFootprintKgs,
    packaging: packagingFootprintKgs,
    travel: routeFootprintKgs,
    energy: energyFootprintKgs
  } = editedFootprintBreakdownKgs || {};

  const isElectronic = isProductElectronic(category, subcategory);

  const isBasicInfoEqual = Object.entries(originalBasicInfo).every(
    ([key, value]) => basicInfo[key] === value
  );

  const originalComponentsNormalized = (resolvedComponents || []).map((c) => ({
    firestoreId: c.id,
    name: c.name,
    materials: c.materials || [],
    productWeight: c.productWeight || 0
  }));
  const editableComponentsNormalized = editableComponents.map(
    ({ firestoreId, name, materials, productWeight }) => ({
      firestoreId,
      name,
      materials: materials || [],
      productWeight: productWeight || 0
    })
  );
  const areComponentsEqual =
    JSON.stringify(editableComponentsNormalized) ===
    JSON.stringify(originalComponentsNormalized);

  const isPackagingEqual = findAreMaterialsArraysEqual(
    originalPackaging || [],
    editablePackaging
  );

  const isElectronicsEqual = isElectronic
    ? Object.entries(originalElectronics || {}).every(
        ([key, value]) => editableElectronics[key] === value
      )
    : true;

  const isRouteEqual =
    JSON.stringify(originalRoute) === JSON.stringify(editableRoute);

  const isEditedProductDifferent = readOnly
    ? sku !== originalSku || additionalIsProductDifferent
    : !isBasicInfoEqual ||
      !areComponentsEqual ||
      !isPackagingEqual ||
      !isElectronicsEqual ||
      !isRouteEqual ||
      editedKgsCo2e !== originalKgsCo2e ||
      additionalIsProductDifferent;

  const pendingBanner = pendingNote && (
    <DetailsActionAlert
      color={palette.text.secondary}
      icon={faTriangleExclamation}
      text={pendingNote}
      typographyVariant="body2"
    />
  );

  const warningBanner = warningNote && (
    <DetailsActionAlert
      color={palette.warning.main}
      icon={faTriangleExclamation}
      text={warningNote}
      typographyVariant="body2"
    />
  );

  const vendorDataVisible = readOnly;

  const views = [
    {
      label: "Specs",
      value: "basicInfo",
      content: (
        <Grid container direction="column" spacing={2}>
          {pendingBanner && <Grid item>{pendingBanner}</Grid>}
          {warningBanner && <Grid item>{warningBanner}</Grid>}
          <Grid item>
            <BasicInfoTab
              lifecycleId={lifecycleId}
              basicInfo={basicInfo}
              setBasicInfo={setBasicInfo}
              isEditedSkuDuplicate={isEditedSkuDuplicate}
              isInApp={isInApp}
              readOnly={readOnly}
              vendorDataVisible={vendorDataVisible}
              allDisabled={disabled}
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: "Components",
      value: "materials",
      content: (
        <Grid container spacing={3} direction="column">
          {pendingBanner && <Grid item>{pendingBanner}</Grid>}
          {isInApp && (
            <Grid item>
              <TabFootprintDisplay
                type="materials"
                footprintKgs={materialsFootprintKgs}
                errorMessage={
                  productWeight
                    ? "Add components to calculate your footprint."
                    : "Enter your product weight in the Specs tab before adding components."
                }
                footprintShared={footprintShared}
              />
            </Grid>
          )}
          <Grid item>
            <ComponentsInterface
              components={editableComponents}
              setComponents={setEditableComponents}
              productWeightUnit={productWeightUnit}
              totalProductWeight={productWeight}
              isElectronic={isElectronic}
              disabled={vendorDataVisible}
              footprintShared={footprintShared}
              componentFootprints={editedComponentFootprints}
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: "Packaging",
      value: "packaging",
      content: (
        <Grid container spacing={3} direction="column">
          {pendingBanner && <Grid item>{pendingBanner}</Grid>}
          <Grid item>
            <MaterialsTab
              type="packaging"
              materials={editablePackaging}
              setMaterials={setEditablePackaging}
              totalFootprintKgs={packagingFootprintKgs}
              productWeightUnit={productWeightUnit}
              totalMaterialsWeight={packagingWeight}
              MaterialComponent={ProductPackagingMaterials}
              isInApp={isInApp}
              disabled={vendorDataVisible}
              footprintShared={footprintShared}
            />
          </Grid>
        </Grid>
      )
    },
    ...(isElectronic
      ? [
          {
            label: "Energy Usage",
            value: "energy",
            content: (
              <Grid container spacing={3} direction="column">
                {pendingBanner && <Grid item>{pendingBanner}</Grid>}
                <Grid item>
                  <EnergyUsageTab
                    electronics={editableElectronics}
                    setElectronics={setEditableElectronics}
                    totalFootprintKgs={energyFootprintKgs}
                    isInApp={isInApp}
                    disabled={vendorDataVisible}
                    footprintShared={footprintShared}
                  />
                </Grid>
              </Grid>
            )
          }
        ]
      : []),
    {
      label: "Transportation",
      value: "routes",
      content: (
        <Grid container spacing={3} direction="column">
          {pendingBanner && <Grid item>{pendingBanner}</Grid>}
          <Grid item>
            <RoutesTab
              route={editableRoute}
              setRoute={setEditableRoute}
              totalFootprintKgs={routeFootprintKgs}
              isInApp={isInApp}
              disabled={vendorDataVisible}
              footprintShared={footprintShared}
            />
          </Grid>
        </Grid>
      )
    },
    ...additionalTabs.map((tab) => ({
      ...tab,
      content:
        typeof tab.content === "function"
          ? tab.content({ editableProduct })
          : tab.content
    }))
  ];

  const viewsWithFooter = views.map((view) => ({
    ...view,
    ...(!disabled && {
      footer: (
        <ProductDetailsFooter
          onSave={onSave}
          isEditedProductDifferent={isEditedProductDifferent}
          saveLoading={saveLoading}
          subcategory={subcategory}
          components={editableComponents}
          packaging={editablePackaging}
          electronics={editableElectronics}
          route={editableRoute}
          isDuplicateSku={isEditedSkuDuplicate}
          productWeight={productWeight}
          packagingWeight={packagingWeight}
          onDelete={onDelete}
          readOnly={readOnly}
          {...basicInfo}
        />
      )
    })
  }));

  return (
    <SettingsObjectDetailsSlider
      type="products"
      otherObjects={otherProducts}
      avatar={
        <Avatar style={{ backgroundColor: palette.secondary.main }}>
          <FontAwesomeIcon icon={faShirt} />
        </Avatar>
      }
      settingsObject={selectedProduct}
      setSelectedObject={setSelectedProduct}
      headerAction={headerAction}
      views={viewsWithFooter}
      onNameSave={(name) => {
        setBasicInfo((prev) => ({ ...prev, name }));
        return updateCollectionDoc("product-components", id, { name });
      }}
      editNameDisabled={!isInApp || readOnly}
      leftContent={
        LeftContentComponent && (
          <LeftContentComponent
            {...leftContentProps}
            basicInfo={basicInfo}
            components={editableComponents}
            packaging={editablePackaging}
            route={editableRoute}
            electronics={editableElectronics}
          />
        )
      }
    />
  );
};
export default ProductDetailsSlider;
